import { useSelector, useDispatch } from 'react-redux';
import {
  useGetUserVTOResultsQuery,
  useGetVTOResultByIdQuery,
  useSaveVTOResultMutation,
  useSaveVTOResultWithUploadsMutation,
  useFavoriteVTOResultMutation,
  useDeleteVTOResultMutation,
  useGetVTOStatsQuery,
  useTogglePublishVTOResultMutation
} from '../redux/apis/endpoints/vtoResultApi';
import {
  startVTOSession,
  updateVTOSession,
  setVTOResult,
  setVTOError,
  clearVTOSession,
  setVTOMode,
  selectVTOResult,
  clearSelectedVTOResult,
  setHistoryFilters,
  setHistoryGarmentTypeFilter,
  setHistoryFavoritesFilter,
  setHistorySortBy,
  setShowHistory,
  updateVTOSettings,
  setProcessingStage,
  setProcessingProgress,
  resetVTOState,
  selectCurrentVTOSession,
  selectVTOSettings,
  selectIsVTOMode,
  selectShowHistory,
  selectSelectedVTOResultId,
  selectHistoryFilters,
  selectIsVTOProcessing,
  selectVTOProgress,
  selectVTOError,
  selectVTOResultImageId,
  selectVTOResultId,
  selectProcessingStage,
  selectCurrentPersonImageId,
  selectCurrentClothingIds,
  selectCurrentSessionId,
  selectCurrentSessionMetadata,
  selectHasVTOResult,
  selectVTOSessionDuration,
  selectHasActiveVTOSession
} from '../redux/slices/vtoSlice';

/**
 * Custom hook that combines RTK Query VTO result data with UI state management
 * Provides a unified interface for virtual try-on operations
 */
export const useVTOResults = () => {
  const dispatch = useDispatch();

  // Get UI state from slice
  const currentSession = useSelector(selectCurrentVTOSession);
  const settings = useSelector(selectVTOSettings);
  const isVTOMode = useSelector(selectIsVTOMode);
  const showHistory = useSelector(selectShowHistory);
  const selectedVTOResultId = useSelector(selectSelectedVTOResultId);
  const historyFilters = useSelector(selectHistoryFilters);
  const isProcessing = useSelector(selectIsVTOProcessing);
  const progress = useSelector(selectVTOProgress);
  const error = useSelector(selectVTOError);
  const resultImageId = useSelector(selectVTOResultImageId);
  const vtoResultId = useSelector(selectVTOResultId);
  const processingStage = useSelector(selectProcessingStage);
  const personImageId = useSelector(selectCurrentPersonImageId);
  const clothingIds = useSelector(selectCurrentClothingIds);
  const sessionId = useSelector(selectCurrentSessionId);
  const sessionMetadata = useSelector(selectCurrentSessionMetadata);
  const hasVTOResult = useSelector(selectHasVTOResult);
  const sessionDuration = useSelector(selectVTOSessionDuration);
  const hasActiveSession = useSelector(selectHasActiveVTOSession);

  // Check authentication before making API calls
  const user = localStorage.getItem('user');
  const token = localStorage.getItem('token');
  const isAuthenticated = !!(user && token);

  // Get VTO result data from RTK Query
  const {
    data: vtoResultsData,
    isLoading: isLoadingVTOResults,
    error: vtoResultsError,
    refetch: refetchVTOResults
  } = useGetUserVTOResultsQuery({
    garmentType: historyFilters.garmentType !== 'all' ? historyFilters.garmentType : undefined,
    favorites: historyFilters.favorites || undefined,
    sort: historyFilters.sortBy,
    limit: historyFilters.limit
  }, {
    skip: !isAuthenticated // Skip query if not authenticated
  });

  const {
    data: selectedVTOResult,
    isLoading: isLoadingSelectedResult,
    error: selectedResultError
  } = useGetVTOResultByIdQuery(selectedVTOResultId, {
    skip: !selectedVTOResultId || !isAuthenticated // Skip if no ID or not authenticated
  });

  const {
    data: currentVTOResult,
    isLoading: isLoadingCurrentResult
  } = useGetVTOResultByIdQuery(vtoResultId, {
    skip: !vtoResultId || !isAuthenticated // Skip if no ID or not authenticated
  });

  const {
    data: vtoStats,
    isLoading: isLoadingStats
  } = useGetVTOStatsQuery(undefined, {
    skip: !isAuthenticated // Skip query if not authenticated
  });

  // RTK Query mutations
  const [saveVTOResult, { isLoading: isSaving }] = useSaveVTOResultMutation();
  const [saveVTOResultWithUploads, { isLoading: isSavingWithUploads }] = useSaveVTOResultWithUploadsMutation();
  const [favoriteVTOResult, { isLoading: isTogglingFavorite }] = useFavoriteVTOResultMutation();
  const [deleteVTOResult, { isLoading: isDeleting }] = useDeleteVTOResultMutation();
  const [togglePublishVTOResult, { isLoading: isPublishing }] = useTogglePublishVTOResultMutation();

  // Get all VTO results with proper transformation
  const getAllVTOResults = () => {
    const results = vtoResultsData?.results || [];

    // console.log('🔍 getAllVTOResults - raw data:', {
    //   hasData: !!vtoResultsData,
    //   resultsCount: results.length,
    //   firstResult: results[0]
    // });

    // Transform results to match expected format, flattening arrays of images
    const transformed = results.flatMap(result => {
      const resultImages = Array.isArray(result.resultImage) ? result.resultImage : (result.resultImage ? [result.resultImage] : []);
      const removedImages = Array.isArray(result.removedBgImage) ? result.removedBgImage : (result.removedBgImage ? [result.removedBgImage] : []);
      
      const count = Math.max(resultImages.length, 1);
      
      const flatResults = Array.from({ length: count }).map((_, i) => {
        const resImg = resultImages[i];
        const remImg = removedImages[i] || removedImages[0];
        
        return {
          ...result,
          id: count > 1 ? `${result.id}_${i}` : result.id,
          originalId: result.id,
          imageUrl: result.imageUrl || remImg?.url || resImg?.url,
          resultImage: resImg || result.resultImage,
          removedBgImage: remImg || result.removedBgImage,
          timestamp: result.created_at || result.createdAt || result.timestamp,
          clothingItems: result.clothingItems || (result.closetClothes ? [result.closetClothes] : [])
        };
      });
      
      // Reverse so that newer generations of the same result appear first
      return flatResults.reverse();
    });

    // console.log('🔍 getAllVTOResults - transformed:', {
    //   count: transformed.length,
    //   firstTransformed: transformed[0]
    // });

    return transformed;
  };

  // Session actions
  const handleStartVTOSession = (personImageId, clothingIds) => {
    dispatch(startVTOSession({ personImageId, clothingIds }));
  };

  const handleUpdateVTOSession = (updates) => {
    dispatch(updateVTOSession(updates));
  };

  const handleSetVTOResult = (resultImageId, vtoResultId, metadata, personImageId) => {
    dispatch(setVTOResult({ resultImageId, vtoResultId, metadata, personImageId }));
  };

  const handleSetVTOError = (error, stage) => {
    dispatch(setVTOError({ error, stage }));
  };

  const handleClearVTOSession = () => {
    dispatch(clearVTOSession());
  };

  const handleSetVTOMode = (enabled) => {
    dispatch(setVTOMode(enabled));
  };

  // History actions
  const handleSelectVTOResult = (resultId) => {
    dispatch(selectVTOResult(resultId));
  };

  const handleClearSelectedVTOResult = () => {
    dispatch(clearSelectedVTOResult());
  };

  // Filter actions
  const handleSetHistoryFilters = (newFilters) => {
    dispatch(setHistoryFilters(newFilters));
  };

  const handleSetHistoryGarmentTypeFilter = (garmentType) => {
    dispatch(setHistoryGarmentTypeFilter(garmentType));
  };

  const handleSetHistoryFavoritesFilter = (favorites) => {
    dispatch(setHistoryFavoritesFilter(favorites));
  };

  const handleSetHistorySortBy = (sortBy) => {
    dispatch(setHistorySortBy(sortBy));
  };

  // UI actions
  const handleSetShowHistory = (show) => {
    dispatch(setShowHistory(show));
  };

  const handleUpdateVTOSettings = (newSettings) => {
    dispatch(updateVTOSettings(newSettings));
  };

  // Processing actions
  const handleSetProcessingStage = (stage) => {
    dispatch(setProcessingStage(stage));
  };

  const handleSetProcessingProgress = (progress) => {
    dispatch(setProcessingProgress(progress));
  };

  // API actions
  const handleSaveVTOResult = async (vtoData) => {
    try {
      const result = await saveVTOResult(vtoData).unwrap();

      // Update current session with saved result ID
      handleSetVTOResult((Array.isArray(result.resultImage) ? result.resultImage[0]?.id : result.resultImage?.id), result.id, result.metadata, result.personImage?.id);

      return result;
    } catch (error) {
      handleSetVTOError(error.message || 'Save failed');
      throw error;
    }
  };

  const handleSaveVTOResultWithUploads = async (vtoData) => {
    try {
      const result = await saveVTOResultWithUploads(vtoData).unwrap();

      // Update current session with saved result ID
      handleSetVTOResult((Array.isArray(result.resultImage) ? result.resultImage[0]?.id : result.resultImage?.id), result.id, result.metadata, result.personImage?.id);

      return result;
    } catch (error) {
      handleSetVTOError(error.message || 'Save with uploads failed');
      throw error;
    }
  };

  const handleFavoriteVTOResult = async (resultId, isFavorite) => {
    try {
      const result = await favoriteVTOResult({ id: resultId, isFavorite }).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      throw error;
    }
  };

  const handleTogglePublish = async (resultId, isPublic) => {
    try {
      const result = await togglePublishVTOResult({ id: resultId, isPublic }).unwrap();
      return result;
    } catch (error) {
      console.error('Failed to toggle publish status:', error);
      throw error;
    }
  };

  const handleDeleteVTOResult = async (resultId) => {
    try {
      await deleteVTOResult(resultId).unwrap();

      // Clear selected result if it was the deleted one
      if (selectedVTOResultId === resultId) {
        handleClearSelectedVTOResult();
      }

      // Clear current session if it references the deleted result
      if (vtoResultId === resultId) {
        handleClearVTOSession();
      }

      return true;
    } catch (error) {
      console.error('Failed to delete VTO result:', error);
      throw error;
    }
  };

  // Helper functions
  const getVTOResultById = (resultId) => {
    const allResults = getAllVTOResults();
    return allResults.find(result => result.id === resultId);
  };

  const getFavoriteVTOResults = () => {
    const allResults = getAllVTOResults();
    return allResults.filter(result => result.isFavorite);
  };

  const getVTOResultsByGarmentType = (garmentType) => {
    const allResults = getAllVTOResults();
    return allResults.filter(result => result.garmentType === garmentType);
  };

  const hasVTOResults = () => {
    return getAllVTOResults().length > 0;
  };

  // Reset state
  const handleResetState = () => {
    dispatch(resetVTOState());
  };

  return {
    // Data
    vtoResults: getAllVTOResults(),
    selectedVTOResult,
    currentVTOResult,
    vtoStats,

    // Session state
    currentSession,
    sessionId,
    sessionMetadata,
    sessionDuration,
    hasActiveSession,
    hasVTOResult,

    // UI State
    isVTOMode,
    showHistory,
    selectedVTOResultId,
    historyFilters,
    settings,

    // Processing state
    isProcessing,
    progress,
    processingStage,
    error,

    // Loading states
    isLoading: isLoadingVTOResults || isLoadingSelectedResult || isLoadingCurrentResult || isLoadingStats,
    isSaving: isSaving || isSavingWithUploads,
    isTogglingFavorite,
    isDeleting,
    isPublishing,

    // Errors
    vtoResultsError,
    selectedResultError,

    // Session actions
    startVTOSession: handleStartVTOSession,
    updateVTOSession: handleUpdateVTOSession,
    setVTOResult: handleSetVTOResult,
    setVTOError: handleSetVTOError,
    clearVTOSession: handleClearVTOSession,
    setVTOMode: handleSetVTOMode,

    // History actions
    selectVTOResult: handleSelectVTOResult,
    clearSelectedVTOResult: handleClearSelectedVTOResult,

    // Filter actions
    setHistoryFilters: handleSetHistoryFilters,
    setHistoryGarmentTypeFilter: handleSetHistoryGarmentTypeFilter,
    setHistoryFavoritesFilter: handleSetHistoryFavoritesFilter,
    setHistorySortBy: handleSetHistorySortBy,

    // UI actions
    setShowHistory: handleSetShowHistory,
    updateVTOSettings: handleUpdateVTOSettings,

    // Processing actions
    setProcessingStage: handleSetProcessingStage,
    setProcessingProgress: handleSetProcessingProgress,

    // API actions
    saveVTOResult: handleSaveVTOResult,
    saveVTOResultWithUploads: handleSaveVTOResultWithUploads,
    favoriteVTOResult: handleFavoriteVTOResult,
    togglePublishVTOResult: handleTogglePublish,
    deleteVTOResult: handleDeleteVTOResult,
    refetchVTOResults,

    // Helpers
    getVTOResultById,
    getFavoriteVTOResults,
    getVTOResultsByGarmentType,
    hasVTOResults,
    resetState: handleResetState
  };
};

export default useVTOResults;